(() => {
  if (window.__EmbryniumProductCardLoaded) return;
  window.__EmbryniumProductCardLoaded = true;

  const normalize = (value) => window.EmbryniumProductState?.normalize?.(value) || String(value || '').toLowerCase().trim();

  const setImage = (image, media, fallbackAlt = '') => {
    if (!image || !media?.src) return false;
    image.removeAttribute('srcset');
    image.removeAttribute('sizes');
    if (image.src !== media.src) image.src = media.src;
    image.alt = media.alt || fallbackAlt;
    image.dataset.mediaId = String(media.id || '');
    if (media.width) image.width = media.width;
    if (media.height) image.height = media.height;
    return true;
  };

  const renderPrice = (node, variant) => {
    if (!node) return;
    node.replaceChildren();
    if (!variant) {
      node.textContent = 'Unavailable';
      return;
    }
    if (Number(variant.compareAtPrice) > Number(variant.price) && variant.compareAtFormatted) {
      const compare = document.createElement('s');
      compare.textContent = variant.compareAtFormatted;
      node.append(compare, document.createTextNode(' '));
    }
    const current = document.createElement('span');
    current.textContent = variant.priceFormatted || '';
    node.append(current);
  };

  /*
    IMPORTANT: Both the catalog card and Quick Shop use this exact media path.
    There is no independent Quick Shop image resolver anymore.
  */
  const resolveSurfaceMedia = (state, selection, variant, requestedView) => {
    const front = state.resolveMedia({ selection, view: 'front', variant });
    const back = state.resolveMedia({ selection, view: 'back', variant });
    const view = requestedView === 'back' && back ? 'back' : 'front';
    return { front, back, view };
  };

  const paintMediaPair = ({ state, selection, variant, requestedView, frontImage, backImage, root, viewSelector }) => {
    const mediaState = resolveSurfaceMedia(state, selection, variant, requestedView);
    setImage(frontImage, mediaState.front, state.product.title);
    if (backImage && mediaState.back) setImage(backImage, mediaState.back, state.product.title);

    if (frontImage) frontImage.hidden = mediaState.view === 'back';
    if (backImage) backImage.hidden = mediaState.view !== 'back' || !mediaState.back;

    root?.querySelectorAll(viewSelector).forEach((button) => {
      const wanted = (button.dataset.embCardView || button.dataset.embProductView) === 'back' ? 'back' : 'front';
      const exists = wanted === 'front' ? Boolean(mediaState.front) : Boolean(mediaState.back);
      button.disabled = !exists;
      button.setAttribute('aria-pressed', String(wanted === mediaState.view));
    });

    return mediaState;
  };

  const initialSelectionFor = (state) => {
    const requested = Array.isArray(state.product.defaultSelection) && state.product.defaultSelection.length
      ? [...state.product.defaultSelection]
      : state.product.options?.map((option) => option.values?.[0] || '') || [];
    const resolved = state.coerceSelection(requested, state.colorIndex);
    return {
      selection: [...(resolved.selection?.length ? resolved.selection : requested)],
      variant: resolved.variant || state.variants.find((item) => item.available) || state.variants[0] || null,
    };
  };


  const initCard = (card) => {
    if (card.dataset.embCardReady === 'true') return;
    const state = window.EmbryniumProductState?.read?.(card.dataset.embConfigId);
    if (!state) return;
    card.dataset.embCardReady = 'true';

    const initial = initialSelectionFor(state);
    let variant = initial.variant;
    let selection = [...initial.selection];
    let view = card.dataset.defaultView || state.product.defaultView || 'front';
    const controls = card.querySelector('[data-emb-card-controls]');
    const frontImage = card.querySelector('.emb-card__image--front');
    const backImage = card.querySelector('.emb-card__image--back');

    const renderCard = () => {
      const resolved = state.coerceSelection(selection);
      selection = resolved.selection;
      variant = resolved.variant;

      const mediaState = paintMediaPair({
        state,
        selection,
        variant,
        requestedView: view,
        frontImage,
        backImage,
        root: controls,
        viewSelector: '[data-emb-card-view]',
      });
      view = mediaState.view;
      card.classList.toggle('is-back', view === 'back');
      card.dataset.embSelection = JSON.stringify(selection);
      card.dataset.embVariantId = variant?.id || '';
      card.dataset.embView = view;

      card.querySelectorAll('[data-emb-card-color]').forEach((button) => {
        button.disabled = !state.valueAvailable(state.colorIndex, button.dataset.embCardColor, selection);
        button.setAttribute('aria-pressed', String(state.colorIndex >= 0 && normalize(button.dataset.embCardColor) === normalize(selection[state.colorIndex])));
      });
      const label = card.querySelector('[data-emb-card-color-label]');
      if (label && state.colorIndex >= 0) label.textContent = selection[state.colorIndex] || '';
      renderPrice(card.querySelector('.emb-card__price'), variant);
    };

    card.classList.add('is-enhanced');
    if (controls) controls.hidden = false;

    controls?.addEventListener('click', (event) => {
      const button = event.target.closest('[data-emb-card-view]');
      if (!button || button.disabled) return;
      view = button.dataset.embCardView === 'back' ? 'back' : 'front';
      renderCard();
    });

    card.addEventListener('click', (event) => {
      const button = event.target.closest('[data-emb-card-color]');
      if (!button || button.disabled || state.colorIndex < 0) return;
      const next = [...selection];
      next[state.colorIndex] = button.dataset.embCardColor;
      const resolved = state.coerceSelection(next, state.colorIndex);
      selection = resolved.selection;
      variant = resolved.variant;
      renderCard();
    });

    renderCard();

    // Expose the card's current state for the standalone Quick Shop entity.
    // Quick Shop is intentionally not initialized here; keeping it separate
    // prevents global/product-card listeners from owning its controls.
  };

  const init = (scope = document) => scope.querySelectorAll('[data-emb-product-card]').forEach(initCard);
  init();
  document.addEventListener('DOMContentLoaded', () => init(), { once: true });
  document.addEventListener('embrynium:products:rendered', (event) => init(event.target || document));
  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
